import { useCallback, useEffect, useRef, useState } from "react";
import { useFormik } from 'formik';
import * as yup from 'yup';
import { toast } from "sonner";
import { ActionIcon, Button, Input, Modal, Switch, Text, Textarea } from "rizzui";
import { IoClose } from "react-icons/io5";
import ReactSignatureCanvas from "react-signature-canvas";
import moment from "moment";
import { IssueService } from "../../../services/issue/issueService";
import useHandleErrors from "../../../hooks/useHandleErrors";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import useIssuesStatus from "../../../hooks/api-calls/useIssuesStatus";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useFetch from "../../../hooks/useFetch";
import EntitySelectors from "../../../components/commonForm/EntitySelectors";
import SignatureBox from "../../../components/signatureBox/SignatureBox";
import RequiredInput from "../../../components/forms/InputHOC";
import CustomSelect from "../../../components/forms/CustomSelect";
import WeatherSelectors from "../../../components/commonForm/WeatherSelectors";
import CoordinatesInputs from "../../../components/commonForm/CoordinatesInputs";
import FormLabel from "../../../components/forms/FormLabel";
import { DatePicker } from "../../../components/forms/DatePicker";
import { age, carrionPresentSelector, sex, stateSpecies, yesNoSelector } from "../../../utils/SelectorMapping";

interface IssuesFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
    issues: string | null;
    isFromPark?: boolean;
    parkId?: string;
}


const schema = yup.object({
    parkId: yup.string().required('Campo requerido'),
    windTurbineId: yup.string().required('Campo requerido'),
    userId: yup.string().required('Campo requerido'),
    specieId: yup.string().required('Campo requerido'),
    windDirection: yup.string().required('Campo requerido'),
    windSpeed: yup.number().required('Campo requerido'),
    cloudCoverage: yup.number().required('Campo requerido'),
    huso: yup.string().required('Campo requerido').test('spain_timezone', 'Debe de ser un huso horario de España (29, 30, 31)', (value) => {
        if (value === null || value === undefined || value === "") return true;
        const regex = /^(29|30|31)/;
        return regex.test(value);
    }),
    coordinatesX: yup.string().required('Campo requerido').test('is-decimal', 'El valor debe de estar entre 160000 y 870000', function (value) {
        if (Number(value) >= 160000 && Number(value) <= 870000) {
            return true;
        } 
        return false;
    }),
    coordinatesY: yup.string().required('Campo requerido').test('is-decimal', 'El valor debe de estar entre 3990000 y 4800000', function (value) {
        if (Number(value) >= 3990000 && Number(value) <= 4800000) {
            return true;
        } 
        return false;
    }),
    dateReported: yup.string().required('Campo requerido'),
    dateEstimated: yup.string()
        .required('Campo requerido')
        .test('not-after-reported', 'La fecha estimada no puede ser posterior a la fecha de reporte', function (value) {
            const { dateReported } = this.parent;
            if (!value || !dateReported) return true;
            const estimated = moment(value, 'DD/MM/YYYY', true);
            const reported = moment(dateReported, 'DD/MM/YYYY', true);
            if (!estimated.isValid() || !reported.isValid()) return true;
            return !estimated.isAfter(reported, 'day');
        }),
    agentIdentifier: yup.string().nullable().notRequired(),
    distTurbine: yup.string()
        .required('Campo requerido')
        .test('min-zero', 'La distancia no puede ser negativa', (value) => {
            if (value === null || value === undefined || value === '') return true;
            return Number(value) >= 0;
        }),
    numIndividuals: yup.string().required('Campo requerido'),
    age: yup.string().required('Campo requerido'),
    sex: yup.string().required('Campo requerido'),
    presenceCarrion: yup.string().required('Campo requerido'),
    askAnillado: yup.string().required('Campo requerido'),
    anillado: yup.string().notRequired(),
    deadHurt: yup.string().required('Campo requerido'),
    injuries: yup.string().required('Campo requerido'),
    habitat: yup.string().required('Campo requerido'),
    workerSignature: yup.string().required('Campo requerido'),
});

const IssuesFormModal: React.FC<IssuesFormModalProps> = ({isOpen, onClose, refetch, issues, isFromPark, parkId }) => {
    
    // STATES

    const signatureRef = useRef<ReactSignatureCanvas | null>(null);
    const signatureCanvasRef = useRef<ReactSignatureCanvas | null>(null);
    const service = new IssueService();
    const isEdit = issues !== null;
    const [isProtected, setIsProtected] = useState<any>(false);
    
    // HOOKS
    
    const { handleErrors } = useHandleErrors();
    const { getCompaniesList } = useCompanies();
    const { getIssuesStatusList } = useIssuesStatus();
    const { userCan } = usePrivilege();

    // DATA FETCHING

    const [data, loading, error] = useFetch(
        useCallback(async () => {
            if (!issues) return;
            const response = await service.getIssueById(issues);
            return response.getResponseData();
        },[issues])
    );

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: '',
            parkId: parkId ? parkId : '',
            windTurbineId: '',
            userId: '', 
            specieId: '',
            statusId: '',
            company: '',
            windDirection: '',
            windSpeed: '',
            cloudCoverage: '',
            huso: '',
            coordinatesX: '',
            coordinatesY: '',
            askAnillado: 'no',
            anillado: '',
            dateReported: '',
            dateEstimated: '',
            comments: '',  
            isProtected: false,
            agentSignature: '',
            agentIdentifier: '',
            photos: null,
            distTurbine: '',
            numIndividuals: '',
            presenceCarrion: '',
            stopNotified: false,
            livestockPresence: false,
            age: '',
            sex: '',
            triedToStop: false,
            deadHurt: '',
            injuries: '',
            workerSignature : '',
            habitat: '',
        },
        validationSchema: schema,
        validateOnBlur: false,
        //validateOnChange: false,
        onSubmit: values => { 
            if(!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });

    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UN NUEVO PARQUE
     * @EN CREATE A NEW PARK
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {

            const formData = new FormData();

            Object.entries(values).forEach(([key, value] : any) => {
                if (value !== null && value !== "" && key !== 'photos' && key !== 'dateReported' && key !== 'dateEstimated' && key !== 'askAnillado') {
                  formData.append(key, value);
                }
                if (key === 'dateReported') {
                    formData.append(key, moment(values.dateReported, 'DD/MM/YYYY').format('YYYY-MM-DD'));
                }
                if (key === 'dateEstimated') {
                    formData.append(key, moment(values.dateEstimated, 'DD/MM/YYYY').format('YYYY-MM-DD'));
                }
            });

            if (values.askAnillado === 'no') {
                formData.append('anillado', '');
            }

            if (values.photos) {
                for (let i = 0; i < values.photos.length; i++) {
                    formData.append(`photos[${i}]`, values.photos[i]);
                }
            }

            const response = await (await service.createIssues(formData)).getResponseData();
            if (response.success) {
                refetch();
                onClose();
                formik.resetForm();
                toast.success('Incidencia creada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------
    

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UN PARQUE
     * @EN EDIT A PARK
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const formData = new FormData();

            Object.entries(values).forEach(([key, value] : any) => {
                if (value !== null && value !== "" && key !== 'photos' && key !== 'dateReported' && key !== 'dateEstimated' && key !== 'askAnillado') {
                  formData.append(key, value);
                }
                if (key === 'dateReported') {
                    formData.append(key, moment(values.dateReported, 'DD/MM/YYYY').format('YYYY-MM-DD'));
                }
                if (key === 'dateEstimated') {
                    formData.append(key, moment(values.dateEstimated, 'DD/MM/YYYY').format('YYYY-MM-DD'));
                }
            });

            if (values.askAnillado === 'no') {
                formData.append('anillado', '');
            }

            formData.append('id', values.id);

            if (values.photos) {
                for (let i = 0; i < values.photos.length; i++) {
                    formData.append(`photos[${i}]`, values.photos[i]);
                }
            }

            const response = await (await service.editIssue(formData)).getResponseData();
            if (response.success) {
                refetch();
                onClose();
                formik.resetForm();
                toast.success('Incidencia editada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    const handleDateChangeReported = (date: any) => {
        const formattedDate = moment(date).format('DD/MM/YYYY');
        formik.setFieldValue('dateReported', formattedDate);

        if (formik.values.dateEstimated) {
            const estimated = moment(formik.values.dateEstimated, 'DD/MM/YYYY', true);
            const reported = moment(formattedDate, 'DD/MM/YYYY', true);
            if (estimated.isValid() && reported.isValid() && estimated.isAfter(reported, 'day')) {
                formik.setFieldValue('dateEstimated', '');
            }
        }
    };

    const handleDateChangeEstimated = (date: any) => {
        const formattedDate = moment(date).format('DD/MM/YYYY');
        formik.setFieldValue('dateEstimated', formattedDate);
    };
    

    // USE EFFECT

    useEffect(() => {
        if (isEdit) {
            formik.setFieldValue('id', data?.id);
            formik.setFieldValue('parkId', data?.park.id);
            formik.setFieldValue('windTurbineId', data?.windturbine?.id);
            formik.setFieldValue('userId', data?.user.id);
            formik.setFieldValue('specieId', data?.species.id);
            formik.setFieldValue('statusId', data?.status.id);
            formik.setFieldValue('company', data?.company);
            formik.setFieldValue('windDirection', data?.windDirection);
            formik.setFieldValue('windSpeed', String(data?.windSpeed));
            formik.setFieldValue('cloudCoverage', String(data?.cloudCoverage));
            let coordinates = data?.coordinates.split(',');
            formik.setFieldValue('huso',coordinates[0]);
            formik.setFieldValue('coordinatesX', coordinates[1]);
            formik.setFieldValue('coordinatesY', coordinates[2]);
            const anilladoValue = data?.anillado == "null" ? '' : (data?.anillado ?? '');
            formik.setFieldValue('anillado', anilladoValue);
            formik.setFieldValue('askAnillado', anilladoValue?.trim() ? 'yes' : 'no');
            // Formatea la fecha al cargar los datos para editar
            formik.setFieldValue('dateReported', data?.dateReported);
            formik.setFieldValue('dateEstimated', moment(data?.dateEstimated.date).format('DD/MM/YYYY'));
            formik.setFieldValue('comments', data?.comments);
            formik.setFieldValue('agentSignature', data?.agentSignature);
            formik.setFieldValue('agentIdentifier', data?.agentIdentifier);
            formik.setFieldValue('isProtected', data?.species.catAndalucia || data?.species.catNacional);
            setIsProtected(data?.species.catAndalucia || data?.species.catNacional);
            formik.setFieldValue('distTurbine', data?.distTurbine);
            formik.setFieldValue('numIndividuals', data?.numIndividuals);
            formik.setFieldValue('presenceCarrion', data?.presenceCarrion);
            formik.setFieldValue('stopNotified', data?.stopNotified);
            formik.setFieldValue('livestockPresence', data?.livestockPresence);
            formik.setFieldValue('triedToStop', data?.triedToStop);
            formik.setFieldValue('age', data?.age);
            formik.setFieldValue('sex', data?.sex);
            formik.setFieldValue('deadHurt', data?.deadHurt);
            formik.setFieldValue('injuries', data?.injuries);
            formik.setFieldValue('habitat', data?.habitat);
            formik.setFieldValue('workerSignature', data?.workerSignature);
        }
        else{
            formik.resetForm();
        }
    }, [data]);

    // RENDER
    
    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                formik.resetForm();
                onClose();
                setIsProtected(false);
            }}
            size='xl'
            overlayClassName='backdrop-blur'
            containerClassName='!max-w-[800px] !shadow-xl'
            className='z-[9999] [&_.pointer-events-none]:overflow-visible'
        >
            <div className='m-auto px-7 pt-6 pb-4'>
                <div className='mb-7 flex items-center justify-between'>
                    <Text className="text-xl">{isEdit ? 'Editar ' : 'Crear '} incidencia</Text>
                    <ActionIcon size='sm' variant='text' onClick={() => {
                        formik.resetForm();
                        setIsProtected(false);
                        onClose();
                    }}>
                        <IoClose className='h-auto w-6' strokeWidth={1.8} />
                    </ActionIcon>
                </div>

                {!isEdit && (
                    <EntitySelectors formik={formik} setIsProtected={setIsProtected} isFromPark={isFromPark} speciesAvailabilityFilter={{ issueAvailable: true }} />
                )}

                {isEdit && (
                    <CustomSelect
                        isSearchable
                        id={'statusId'}
                        label="Estado"
                        options={getIssuesStatusList()}
                        onChange={(e: any) => {
                            formik.setFieldValue('statusId', e.value)
                        }}
                        value={
                            getIssuesStatusList().filter((status: any) => status.value === formik.values.statusId)
                        }
                        required
                        className="mb-4"
                    />
                )}

                {isProtected && (
                    <div className="mb-4 mt-3">
                        <span className="rizzui-input-label block text-sm mb-1.5 font-medium">Firma del AMA
                            {/* <span className="text-red-500">*</span> */}
                        </span>
                        <SignatureBox
                                onSignatureComplete={(base64) => {
                                    formik.setFieldValue('agentSignature', base64);
                                }}
                                signatureRef={signatureRef}
                            />
                        {formik.values.agentSignature && (
                            <div className="mt-2">
                                <p className="text-sm text-gray-600">Firma registrada correctamente</p>
                                <img
                                    src={formik.values.agentSignature}
                                    alt="Firma"
                                    className="mt-2 w-32 h-16 border border-gray-300 rounded-md"
                                />
                            </div>
                        )}

                    <Input
                        id='agentIdentifier'
                        label="Identificador del agente de AMA"
                        className="mb-4 mt-2"
                        value={formik.values.agentIdentifier}
                        onChange={(e) => formik.setFieldValue('agentIdentifier', e.target.value)}
                        error={formik.errors.agentIdentifier && formik.touched.agentIdentifier ? formik.errors.agentIdentifier : undefined}
                        placeholder="Introduce el identificador del agente de AMA"
                        required
                    />
                    </div>


                )} 

                <WeatherSelectors formik={formik} required syncCalmWind />

                <CoordinatesInputs formik={formik} />
                    
                <div className="flex flex-col mb-4">
                    <FormLabel label="Fecha de reporte" required> 
                        <DatePicker
                            className={formik.errors.dateReported && formik.touched.dateReported ? 'invalid-form-input w-full' : 'w-full'}
                            placeholderText="Selecciona la fecha"
                            selected={formik.values.dateReported ? moment(formik.values.dateReported, 'DD/MM/YYYY').toDate() : null}
                            onChange={handleDateChangeReported}
                            dateFormat="dd/MM/yyyy"
                            required
                        />    
                    </FormLabel>
                    {formik.errors.dateReported && formik.touched.dateReported && (
                        <Text className="text-red-500 text-sm mt-1">
                            {formik.errors.dateReported}
                        </Text>
                    )} 
                </div>       

                <div className="flex flex-col mb-4">
                    <FormLabel label="Fecha de estimada del accidente" required> 
                        <DatePicker
                            className={formik.errors.dateEstimated && formik.touched.dateEstimated ? 'invalid-form-input w-full' : 'w-full'}
                            placeholderText="Selecciona la fecha"
                            selected={formik.values.dateEstimated ? moment(formik.values.dateEstimated, 'DD/MM/YYYY').toDate() : null}
                            onChange={handleDateChangeEstimated}
                            dateFormat="dd/MM/yyyy"
                            maxDate={formik.values.dateReported ? moment(formik.values.dateReported, 'DD/MM/YYYY').toDate() : new Date()}
                            required
                        />    
                    </FormLabel>
                    {formik.errors.dateEstimated && formik.touched.dateEstimated && (
                        <Text className="text-red-500 text-sm mt-1">
                            {formik.errors.dateEstimated}
                        </Text>
                    )} 
                </div>       
                
                {userCan('list', 'companies') ? (
                    <CustomSelect
                        isSearchable
                        id={'company'}
                        label="Compañia"
                        options={getCompaniesList()}
                        onChange={(e: any) => { 
                            formik.setFieldValue('company', e.value)
                        }}
                        value={
                            getCompaniesList().filter((company: any) => company.value === formik.values.company)
                        }
                        className="mb-4"
                    />
                ) : null}

                <div className="grid md:grid-cols-2 grid-cols-1 mb-4 gap-2">
                    <div className="flex flex-col">
                        <RequiredInput
                            id='distTurbine'
                            label="Distancia al aerogenerador"
                            formik={formik}
                            required
                            className="mb-4"
                            type={'number'}
                            min={0}
                        />
                    </div>
                    <div className="flex flex-col">
                        <RequiredInput
                            id='numIndividuals'
                            label="Número de individuos"
                            formik={formik}
                            required
                            min={0}
                            className="mb-4"
                            type={'number'}
                        />
                    </div>
                </div>

                <div className="grid md:grid-cols-3 grid-cols-1 mb-4 gap-2">
                        <CustomSelect
                            isSearchable
                            id={'presenceCarrion'}
                            label="Presencia de carroña"
                            options={carrionPresentSelector}
                            onChange={(e: any) => {
                                formik.setFieldValue('presenceCarrion', e.value)
                            }}
                            value={
                                formik.values.presenceCarrion
                                ? carrionPresentSelector.find(option => option.value == formik.values.presenceCarrion)
                                : null
                            }
                            required
                            error={formik.errors.presenceCarrion && formik.touched.presenceCarrion ? formik.errors.presenceCarrion : undefined}
                        />


                        <CustomSelect
                            isSearchable
                            id={'age'}
                            label="Edad del ejemplar"
                            options={age}
                            onChange={(e: any) => {
                                formik.setFieldValue('age', e.value)
                            }}
                            value={
                                formik.values.age
                                    ? age.find(option => option.value == formik.values.age as string)
                                    : null
                            }
                            required={true}
                            error={formik.errors.age && formik.touched.age ? formik.errors.age : undefined}
                        />

                        <CustomSelect
                            isSearchable
                            id={'sex'}
                            label="Sexo del ejemplar"
                            options={sex}
                            onChange={(e: any) => {
                                formik.setFieldValue('sex', e.value)
                            }}
                            value={
                                formik.values.sex
                                    ? sex.find(option => option.value === formik.values.sex)
                                    : null
                            }
                            required={true}
                            error={formik.errors.sex && formik.touched.sex ? formik.errors.sex : undefined}
                        />
                    <CustomSelect
                        isSearchable
                        id={'deadHurt'}
                        label="Estado del ejemplar"
                        options={stateSpecies}
                        onChange={(e: any) => {
                            formik.setFieldValue('deadHurt', e.value)
                        }}
                        value={
                            formik.values.deadHurt
                                ? stateSpecies.find(option => option.value === formik.values.deadHurt)
                                : null
                        }
                        required={true}
                        error={formik.errors.deadHurt && formik.touched.deadHurt ? formik.errors.deadHurt : undefined}
                    />

                    <div className="flex flex-col">
                        <RequiredInput
                            id='injuries'
                            label="Lesiones"
                            formik={formik}
                            required
                            min={0}
                            className="mb-4"
                        />
                    </div>

                    <div className="flex flex-col">
                        <RequiredInput
                            id='habitat'
                            label="Habitat"
                            formik={formik}
                            required
                            error={formik.errors.habitat && formik.touched.habitat ? formik.errors.habitat : undefined}
                            min={0}
                            className="mb-4"
                        />
                    </div>

                </div>


                <div className="grid md:grid-cols-3 grid-cols-1 gap-4 mt-2">
                    <Switch
                        id='stopNotified'
                        label="Parada notificada"
                        checked={formik.values.stopNotified}
                        onChange={(e) => formik.setFieldValue('stopNotified', e.target.checked)}
                        className="mb-4"
                    />

                    <Switch
                        id='livestockPresence'
                        label="Presencia de ganado"
                        checked={formik.values.livestockPresence}
                        onChange={(e) => formik.setFieldValue('livestockPresence', e.target.checked)}
                        className="mb-4"
                    />

                    <Switch
                        id='triedToStop'
                        label="Intento hacer parada"
                        checked={formik.values.triedToStop}
                        onChange={(e) => formik.setFieldValue('triedToStop', e.target.checked)}
                        className="mb-4"
                    />
                </div>
                
                
                <div className="grid md:grid-cols-2 grid-cols-1 mb-4 gap-2">
                    <div className="flex flex-col">
                        <CustomSelect
                            isSearchable
                            id={'askAnillado'}
                            label="Anillado"
                            options={yesNoSelector}
                            onChange={(e: any) => {
                                formik.setFieldValue('askAnillado', e.value);
                                if (e.value === 'no') {
                                    formik.setFieldValue('anillado', '');
                                }
                            }}
                            value={
                                formik.values.askAnillado
                                    ? yesNoSelector.find(option => option.value === formik.values.askAnillado)
                                    : yesNoSelector.find(option => option.value === 'no')
                            }
                            required
                            error={formik.errors.askAnillado && formik.touched.askAnillado ? formik.errors.askAnillado : undefined}
                        />
                        {formik.values.askAnillado === 'yes' && (
                            <div className="mt-4">
                                <FormLabel label="Detalles del anillado">
                                    <Textarea
                                        id='anillado'
                                        rows={6}
                                        value={formik.values.anillado}
                                        onChange={(e) => formik.setFieldValue('anillado', e.target.value)}
                                        className="mb-4"
                                        error={formik.errors.anillado && formik.touched.anillado ? formik.errors.anillado : undefined}
                                    />
                                </FormLabel>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col">
                        <FormLabel label="Observaciones">
                            <Textarea
                                id='comments'
                                rows={6}
                                value={formik.values.comments}
                                onChange={(e) => formik.setFieldValue('comments', e.target.value)}
                                className={formik.errors.comments && formik.touched.comments ? 'invalid-form-input' : ''}
                            />
                        </FormLabel>
                    </div>

                </div>

                {isEdit ? null : <div className='mb-4 mt-2'>
                    <FormLabel label="Fotos de la incidencia"> 
                        <input
                            type="file"
                            id="photos"
                            name="photos"
                            accept="image/*"
                            multiple
                            onChange={(event) => {
                                const files = event?.currentTarget?.files ? event.currentTarget.files : null;
                                formik.setFieldValue("photos", files);
                            }}
                            className='mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700'
                        />
                        {formik.errors.photos && formik.touched.photos && (
                            <Text className="text-red-500 text-sm mt-1">
                                {formik.errors.photos}
                            </Text>
                        )}
                    </FormLabel>
                </div> }

                <div className="mb-4 mt-3">
                    <span className="rizzui-input-label block text-sm mb-1.5 font-medium">Firma del técnico
                        {/* <span className="text-red-500">*</span> */}
                    </span>
                    <SignatureBox
                            onSignatureComplete={(base64) => {
                                formik.setFieldValue('workerSignature', base64);
                            }}
                            signatureRef={signatureCanvasRef}
                        />
                    {formik.errors.workerSignature && formik.touched.workerSignature && (
                        <Text className="text-red-500 text-sm mt-1">
                            {formik.errors.workerSignature}
                        </Text>
                    )}
                    {formik.values.workerSignature && (
                        <div className="mt-2">
                            <p className="text-sm text-gray-600">Firma registrada correctamente</p>
                            <img
                                src={formik.values.workerSignature}
                                alt="Firma"
                                className="mt-2 w-32 h-16 border border-gray-300 rounded-md"
                            />
                        </div>
                    )}
                </div>

            </div>

            <div className='flex justify-center p-7'>
                <Button
                    disabled={false}
                    onClick={() => {console.log('formik.errors', formik.errors); formik.handleSubmit()}}
                    color='primary'
                    >
                        {isEdit ? 'Editar' : 'Crear'}
                    </Button>
                </div>
            </Modal>
        );
    };

    export default IssuesFormModal;
